// src/utils/mockDataEstimating.ts
// Estimating & Tender module — client documents, BOQ, tenders, markups

// ── Types ─────────────────────────────────────────────────────────

export type TenderStatus =
  | 'DRAFT' | 'IN_PREPARATION' | 'PENDING_PM_REVIEW' | 'REVISION_REQUESTED'
  | 'READY_TO_SUBMIT' | 'SUBMITTED' | 'PENDING_DECISION'
  | 'AWARDED' | 'UNSUCCESSFUL' | 'WITHDRAWN'

export type DocumentType =
  | 'TENDER_DRAWING' | 'SPECIFICATION' | 'MATERIAL_SCHEDULE'
  | 'WORK_SCHEDULE' | 'RFT_PACKAGE' | 'OTHER'

export type BOQSection =
  | 'CIVIL' | 'ELECTRICAL' | 'HV_ELECTRICAL'
  | 'TRAFFIC_CONTROL' | 'COMMISSIONING' | 'PRELIM_GENERAL'

export type BOQItemType =
  | 'LABOUR' | 'MATERIAL' | 'SUBCONTRACTOR' | 'PRELIM' | 'CONTINGENCY' | 'MARGIN'

export type TenderSectionCode = 'A'|'B'|'C'|'D'|'E'|'F'|'G'|'H'|'I'|'J'

// ── Display metadata ───────────────────────────────────────────────

export const TENDER_STATUS_META: Record<TenderStatus, { label: string; bg: string; text: string }> = {
  DRAFT:              { label: 'Draft',                bg: 'bg-gray-100',   text: 'text-gray-600'  },
  IN_PREPARATION:     { label: 'In preparation',       bg: 'bg-blue-100',   text: 'text-blue-700'  },
  PENDING_PM_REVIEW:  { label: 'Pending PM review',    bg: 'bg-amber-100',  text: 'text-amber-700' },
  REVISION_REQUESTED: { label: 'Revision requested',   bg: 'bg-orange-100', text: 'text-orange-700'},
  READY_TO_SUBMIT:    { label: 'Ready to submit',      bg: 'bg-sky-100',    text: 'text-sky-700'   },
  SUBMITTED:          { label: 'Submitted',            bg: 'bg-purple-100', text: 'text-purple-700'},
  PENDING_DECISION:   { label: 'Pending decision',     bg: 'bg-indigo-100', text: 'text-indigo-700'},
  AWARDED:            { label: 'Awarded ✓',            bg: 'bg-green-100',  text: 'text-green-700' },
  UNSUCCESSFUL:       { label: 'Unsuccessful',         bg: 'bg-red-100',    text: 'text-red-600'   },
  WITHDRAWN:          { label: 'Withdrawn',            bg: 'bg-gray-100',   text: 'text-gray-400'  },
}

export const DOC_TYPE_META: Record<DocumentType, { label: string; bg: string; text: string; icon: string }> = {
  TENDER_DRAWING:   { label: 'Tender drawing',     bg: 'bg-blue-100',   text: 'text-blue-800',  icon: '📐' },
  SPECIFICATION:    { label: 'Specification',       bg: 'bg-purple-100', text: 'text-purple-800',icon: '📋' },
  MATERIAL_SCHEDULE:{ label: 'Material schedule',   bg: 'bg-teal-100',   text: 'text-teal-800',  icon: '📦' },
  WORK_SCHEDULE:    { label: 'Work schedule',       bg: 'bg-amber-100',  text: 'text-amber-800', icon: '🗓' },
  RFT_PACKAGE:      { label: 'RFT package',         bg: 'bg-red-100',    text: 'text-red-800',   icon: '📁' },
  OTHER:            { label: 'Other',               bg: 'bg-gray-100',   text: 'text-gray-600',  icon: '📄' },
}

export const BOQ_SECTION_META: Record<BOQSection, { label: string; color: string }> = {
  CIVIL:         { label: 'Civil works',              color: 'text-teal-700'  },
  ELECTRICAL:    { label: 'Electrical',               color: 'text-blue-700'  },
  HV_ELECTRICAL: { label: 'HV Electrical',            color: 'text-red-700'   },
  TRAFFIC_CONTROL:{ label: 'Traffic control',         color: 'text-amber-700' },
  COMMISSIONING: { label: 'Commissioning',            color: 'text-purple-700'},
  PRELIM_GENERAL:{ label: 'Preliminary & General',    color: 'text-gray-700'  },
}

export const TENDER_SECTIONS: { code: TenderSectionCode; title: string }[] = [
  { code: 'A', title: 'Cover sheet'          },
  { code: 'B', title: 'Executive summary'    },
  { code: 'C', title: 'Scope of works'       },
  { code: 'D', title: 'Method statement'     },
  { code: 'E', title: 'Bill of quantities'   },
  { code: 'F', title: 'Tender price summary' },
  { code: 'G', title: 'Qualifications'       },
  { code: 'H', title: 'Construction program' },
  { code: 'I', title: 'Key personnel'        },
  { code: 'J', title: 'Appendices'           },
]

// ── Data interfaces ────────────────────────────────────────────────

export interface MarkupConfig {
  labourOverheadPct:      number   // default 20
  materialMarginPct:      number   // default 15
  subcontractorMarginPct: number   // default 10
  contingencyPct:         number   // default 5
  profitMarginPct:        number   // default 8
}

export interface BOQItem {
  id: string
  sectionCode: BOQSection
  type: BOQItemType
  description: string
  quantity: number
  unit: string
  unitCostBase: number      // cost before markup
  markupPct: number
  unitRateCharged: number   // cost with markup
  totalCharged: number      // qty × unitRateCharged
  sopRef?: string           // if labour, SOP it was priced from
  tradeCode?: string        // trade type for labour lines
  isManualOverride: boolean
  manualOverrideNote?: string
  // ── Client cost code fields (CostCode_FunctionSpec Phase 2) ──────
  // Optional until cost code library is imported for this client.
  // mrsGroup + workItemId together identify the client schedule item.
  // clientScheduleRate is the client's published rate (ex GST);
  // unitRateCharged is MCB CE's charge-out rate (after markup).
  mrsGroup?: string                              // e.g. 'MRS93'
  workItemId?: string                            // e.g. '93001L'
  clientScheduleRate?: number                    // client's schedule rate ex GST
  costCodeMatchConfidence?: 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE'
  costCodeNote?: string                          // AI match note or manual selection note
}

export interface ClientDocument {
  id: string
  jobId: string
  filename: string
  type: DocumentType
  drawingNumber?: string
  revisionCode?: string
  title: string
  receivedDate: string
  uploadedById: string
  uploadedByName: string
  linkedTenderIds: string[]
  discipline?: string
  notes?: string
  fileSize: string
}

export interface TenderSectionContent {
  code: TenderSectionCode
  content: string           // editable rich text (plain text for mock)
  isComplete: boolean
  lastEditedById?: string
  lastEditedAt?: string
}

export interface Tender {
  id: string
  reference: string         // TDR-2026-001
  jobId: string
  rftReference: string      // Client's RFT number
  clientName: string
  clientType: string
  projectName: string
  status: TenderStatus
  submissionDeadline?: string
  validityPeriodDays: number
  sourceDocumentIds: string[]
  markupConfig: MarkupConfig
  boqItems: BOQItem[]
  sections: TenderSectionContent[]
  // Calculated summary (pre-computed from boqItems)
  summary: TenderSummary
  // Workflow
  submittedAt?: string
  submittedById?: string
  submittedByName?: string
  pmReviewNotes?: string
  revisionNotes?: string
  outcomeRecordedAt?: string
  awardedAmount?: number
  // Meta
  createdById: string
  createdByName: string
  createdAt: string
  updatedAt: string
}

export interface TenderSummary {
  directLabour:         number   // labour before overhead
  labourWithOverhead:   number
  directMaterials:      number   // materials before margin
  materialsWithMargin:  number
  subcontractors:       number
  subtotalDirect:       number
  prelimGeneral:        number
  contingency:          number
  profitMargin:         number
  tenderExGST:          number
  gst:                  number
  tenderIncGST:         number
}

// ── Helper: calculate summary ──────────────────────────────────────

export function calcTenderSummary(items: BOQItem[], cfg: MarkupConfig): TenderSummary {
  const labour   = items.filter(i => i.type === 'LABOUR').reduce((s, i) => s + i.quantity * i.unitCostBase, 0)
  const mats     = items.filter(i => i.type === 'MATERIAL').reduce((s, i) => s + i.totalCharged, 0)
  const subs     = items.filter(i => i.type === 'SUBCONTRACTOR').reduce((s, i) => s + i.totalCharged, 0)
  const prelim   = items.filter(i => i.type === 'PRELIM').reduce((s, i) => s + i.totalCharged, 0)

  const labourOH     = labour * (1 + cfg.labourOverheadPct / 100)
  const subtotalDir  = labourOH + mats + subs
  const contingency  = (subtotalDir + prelim) * (cfg.contingencyPct / 100)
  const beforeMargin = subtotalDir + prelim + contingency
  const margin       = beforeMargin * (cfg.profitMarginPct / 100)
  const exGST        = beforeMargin + margin
  const gst          = exGST * 0.1

  return {
    directLabour:         Math.round(labour),
    labourWithOverhead:   Math.round(labourOH),
    directMaterials:      Math.round(mats / (1 + cfg.materialMarginPct / 100)),
    materialsWithMargin:  Math.round(mats),
    subcontractors:       Math.round(subs),
    subtotalDirect:       Math.round(subtotalDir),
    prelimGeneral:        Math.round(prelim),
    contingency:          Math.round(contingency),
    profitMargin:         Math.round(margin),
    tenderExGST:          Math.round(exGST),
    gst:                  Math.round(gst),
    tenderIncGST:         Math.round(exGST + gst),
  }
}

// ── Mock client documents ──────────────────────────────────────────

export const MOCK_CLIENT_DOCUMENTS: ClientDocument[] = [
  {
    id: 'doc-001', jobId: 'j1', filename: 'J1-E01-Signal-Layout-RevB.pdf',
    type: 'TENDER_DRAWING', drawingNumber: 'J1-E01', revisionCode: 'B',
    title: 'Sandgate Rd / Earnshaw Rd — Signal layout plan',
    receivedDate: '2025-11-20', uploadedById: 'u2', uploadedByName: 'Jane Smith',
    linkedTenderIds: ['tdr-001'],
    discipline: 'ELECTRICAL', fileSize: '2.4 MB',
    notes: 'Primary signal layout drawing. References MRTS93 throughout.',
  },
  {
    id: 'doc-002', jobId: 'j1', filename: 'J1-C01-Civil-Footing-Schedule.pdf',
    type: 'TENDER_DRAWING', drawingNumber: 'J1-C01', revisionCode: 'A',
    title: 'Sandgate Rd — Signal pole footing schedule',
    receivedDate: '2025-11-20', uploadedById: 'u2', uploadedByName: 'Jane Smith',
    linkedTenderIds: ['tdr-001'],
    discipline: 'CIVIL', fileSize: '1.1 MB',
    notes: 'Footing types F-1 through F-4 per MRTS92. RPEQ-certified.',
  },
  {
    id: 'doc-003', jobId: 'j1', filename: 'TMR-RFT-CS2025-04417-Full-Package.pdf',
    type: 'RFT_PACKAGE', title: 'TMR RFT CS-2025-04417 — Complete tender package',
    receivedDate: '2025-11-18', uploadedById: 'u2', uploadedByName: 'Jane Smith',
    linkedTenderIds: ['tdr-001'],
    fileSize: '14.7 MB',
    notes: 'Full RFT package including specification schedule, special conditions, pricing form.',
  },
  {
    id: 'doc-004', jobId: 'j3', filename: 'SCC-RFT-CIVIL-2026-0041-Package.pdf',
    type: 'RFT_PACKAGE', title: 'SCC RFT CIVIL-2026-0041 — Maroochydore Rd Intersection',
    receivedDate: '2026-02-10', uploadedById: 'u4', uploadedByName: 'Sarah Lee',
    linkedTenderIds: ['tdr-003'],
    fileSize: '22.1 MB',
    notes: 'Full council RFT package. Tender closing date 28 June 2026.',
  },
  {
    id: 'doc-005', jobId: 'j3', filename: 'SCC-J3-Drawing-Register.pdf',
    type: 'TENDER_DRAWING', drawingNumber: 'J3-DWG-001', revisionCode: 'A',
    title: 'Maroochydore Rd — Civil works drawing register (19 drawings)',
    receivedDate: '2026-02-10', uploadedById: 'u4', uploadedByName: 'Sarah Lee',
    linkedTenderIds: ['tdr-003'],
    discipline: 'CIVIL', fileSize: '8.3 MB',
  },
  {
    id: 'doc-006', jobId: 'j3', filename: 'SCC-Material-Schedule-Maroochydore.xlsx',
    type: 'MATERIAL_SCHEDULE', title: 'Maroochydore Rd — Council supply material schedule',
    receivedDate: '2026-02-12', uploadedById: 'u4', uploadedByName: 'Sarah Lee',
    linkedTenderIds: ['tdr-003'],
    fileSize: '0.4 MB',
    notes: 'Council-supplied materials: traffic signal heads × 24, controller × 1. MCB CE to supply all civil materials.',
  },
  {
    id: 'doc-007', jobId: 'j3', filename: 'SCC-Works-Programme-Indicative.pdf',
    type: 'WORK_SCHEDULE', title: 'Maroochydore Rd — Indicative construction programme (client)',
    receivedDate: '2026-02-15', uploadedById: 'u4', uploadedByName: 'Sarah Lee',
    linkedTenderIds: ['tdr-003'],
    fileSize: '0.8 MB',
    notes: 'Client-issued indicative programme: 9-month construction period, March 2026 start target.',
  },
  {
    id: 'doc-008', jobId: 'j4', filename: 'TMR-RFT-ITS-2026-00934.pdf',
    type: 'RFT_PACKAGE', title: 'TMR RFT ITS-2026-00934 — Virginia ITS Node Commissioning',
    receivedDate: '2026-03-01', uploadedById: 'u2', uploadedByName: 'Jane Smith',
    linkedTenderIds: [],
    fileSize: '6.2 MB',
    notes: 'New RFT — not yet linked to a tender. Click "Start tender" to begin.',
  },
]

// ── Mock tenders ───────────────────────────────────────────────────

const defaultMarkup: MarkupConfig = {
  labourOverheadPct: 20, materialMarginPct: 15,
  subcontractorMarginPct: 10, contingencyPct: 5, profitMarginPct: 8,
}

// Tender 1: AWARDED — TP-2026-001 Sandgate Rd signals
const tdr001BOQ: BOQItem[] = [
  { id:'b01', sectionCode:'CIVIL', type:'LABOUR', description:'Signal pole foundation install (Type F-2) per MRTS92 — labour', quantity:8, unit:'each', unitCostBase:320, markupPct:20, unitRateCharged:384, totalCharged:3072, sopRef:'wi-sop-001', tradeCode:'CL2', isManualOverride:false, mrsGroup:'MRS92', workItemId:'92002L', clientScheduleRate:320, costCodeMatchConfidence:'HIGH', costCodeNote:'WI ref match: TMR MRS SD ITS-0092' },
  { id:'b02', sectionCode:'CIVIL', type:'MATERIAL', description:'Ready-mix concrete Grade 40 supply — foundations', quantity:8, unit:'each', unitCostBase:680, markupPct:15, unitRateCharged:782, totalCharged:6256, isManualOverride:false, mrsGroup:'MRS92', workItemId:'92002M', clientScheduleRate:680, costCodeMatchConfidence:'HIGH' },
  { id:'b03', sectionCode:'CIVIL', type:'LABOUR', description:'UPVC 100mm conduit install per MRTS91 — labour', quantity:420, unit:'m', unitCostBase:8, markupPct:20, unitRateCharged:9.6, totalCharged:4032, sopRef:'wi-sop-002', tradeCode:'CL1', isManualOverride:false, mrsGroup:'MRS91', workItemId:'91001L', clientScheduleRate:8, costCodeMatchConfidence:'HIGH', costCodeNote:'WI ref match: TMR MRS SD ITS-0091' },
  { id:'b04', sectionCode:'CIVIL', type:'MATERIAL', description:'UPVC 100mm conduit + fittings supply', quantity:420, unit:'m', unitCostBase:18, markupPct:15, unitRateCharged:20.7, totalCharged:8694, isManualOverride:false, mrsGroup:'MRS91', workItemId:'91001M', clientScheduleRate:18, costCodeMatchConfidence:'HIGH' },
  { id:'b05', sectionCode:'CIVIL', type:'MATERIAL', description:'Cable pit UPVC 300×450 with trafficable lid supply & install', quantity:12, unit:'each', unitCostBase:620, markupPct:15, unitRateCharged:713, totalCharged:8556, isManualOverride:false, mrsGroup:'MRS91', workItemId:'91010C', clientScheduleRate:950, costCodeMatchConfidence:'MEDIUM', costCodeNote:'Combined supply+install code applied — confirm against TMR template' },
  { id:'b06', sectionCode:'ELECTRICAL', type:'LABOUR', description:'3C+E 6mm² cable pull & terminate per MRTS256 — labour', quantity:850, unit:'m', unitCostBase:6, markupPct:20, unitRateCharged:7.2, totalCharged:6120, sopRef:'wi-sop-003', tradeCode:'EL2', isManualOverride:false, mrsGroup:'MRS256', workItemId:'256001L', clientScheduleRate:6, costCodeMatchConfidence:'HIGH', costCodeNote:'WI ref match: TMR MRS SD ITS-0256' },
  { id:'b07', sectionCode:'ELECTRICAL', type:'MATERIAL', description:'3C+E 6mm² Olex power cable supply', quantity:850, unit:'m', unitCostBase:14, markupPct:15, unitRateCharged:16.1, totalCharged:13685, isManualOverride:false, mrsGroup:'MRS256', workItemId:'256001M', clientScheduleRate:14, costCodeMatchConfidence:'HIGH' },
  { id:'b08', sectionCode:'ELECTRICAL', type:'LABOUR', description:'LED signal head 3-aspect mount & align per MRTS253 — labour', quantity:24, unit:'each', unitCostBase:380, markupPct:20, unitRateCharged:456, totalCharged:10944, sopRef:'wi-sop-004', tradeCode:'EL2', isManualOverride:false, mrsGroup:'MRS93', workItemId:'93001L', clientScheduleRate:380, costCodeMatchConfidence:'HIGH', costCodeNote:'WI ref match: TMR MRS SD ITS-0253' },
  { id:'b09', sectionCode:'ELECTRICAL', type:'MATERIAL', description:'LED 3-aspect signal head (TMR TAER approved) supply', quantity:24, unit:'each', unitCostBase:680, markupPct:15, unitRateCharged:782, totalCharged:18768, isManualOverride:false, mrsGroup:'MRS93', workItemId:'93001M', clientScheduleRate:680, costCodeMatchConfidence:'HIGH' },
  { id:'b10', sectionCode:'ELECTRICAL', type:'LABOUR', description:'R900S controller cabinet fit-out & SCATS config per MRTS255 — labour', quantity:1, unit:'item', unitCostBase:3600, markupPct:20, unitRateCharged:4320, totalCharged:4320, sopRef:'wi-sop-005', tradeCode:'EL2', isManualOverride:false, mrsGroup:'MRS93', workItemId:'93010L', clientScheduleRate:3600, costCodeMatchConfidence:'HIGH', costCodeNote:'WI ref match: TMR MRS SD ITS-0255' },
  { id:'b11', sectionCode:'ELECTRICAL', type:'MATERIAL', description:'R900S SCATS controller cabinet supply (from TMR nominated supplier)', quantity:1, unit:'item', unitCostBase:14800, markupPct:15, unitRateCharged:17020, totalCharged:17020, isManualOverride:false, mrsGroup:'MRS93', workItemId:'93010M', clientScheduleRate:14800, costCodeMatchConfidence:'HIGH' },
  { id:'b12', sectionCode:'COMMISSIONING', type:'LABOUR', description:'SCATS functional test & TMR ITS acceptance per MRTS93 cl. 8 — labour', quantity:1, unit:'item', unitCostBase:2800, markupPct:20, unitRateCharged:3360, totalCharged:3360, sopRef:'wi-sop-011', tradeCode:'EL3', isManualOverride:false, mrsGroup:'MRS93', workItemId:'93020L', clientScheduleRate:2800, costCodeMatchConfidence:'HIGH', costCodeNote:'WI ref match: TMR MRS SD ITS-0093' },
  { id:'b13', sectionCode:'TRAFFIC_CONTROL', type:'PRELIM', description:'Traffic control — single-lane alternating per MRTS93 & TCP plan', quantity:25, unit:'days', unitCostBase:880, markupPct:0, unitRateCharged:880, totalCharged:22000, isManualOverride:false, mrsGroup:'MRS02', workItemId:'20019C', clientScheduleRate:880, costCodeMatchConfidence:'HIGH' },
  { id:'b14', sectionCode:'PRELIM_GENERAL', type:'PRELIM', description:'Site supervision / PC — project duration', quantity:8, unit:'weeks', unitCostBase:2400, markupPct:0, unitRateCharged:2400, totalCharged:19200, isManualOverride:false, mrsGroup:'MRS28', workItemId:'28002C', clientScheduleRate:2400, costCodeMatchConfidence:'HIGH' },
  { id:'b15', sectionCode:'PRELIM_GENERAL', type:'PRELIM', description:'Site establishment, mob/demob, safety plan, SWMS', quantity:1, unit:'item', unitCostBase:4500, markupPct:0, unitRateCharged:4500, totalCharged:4500, isManualOverride:false, mrsGroup:'MRS28', workItemId:'28001C', clientScheduleRate:4500, costCodeMatchConfidence:'HIGH' },
]

const tdr001Sections: TenderSectionContent[] = [
  { code:'A', isComplete:true, content:'MCB Civil & Electrical Pty Ltd\nABN: 44 128 993 087\nTender Reference: TDR-2026-001\nClient: Department of Transport and Main Roads\nRFT Reference: TMR-CS-2025-04417\nProject: Sandgate Rd / Earnshaw Rd Traffic Signal Upgrade\nDate of Tender: 10 December 2025\nTender Validity: 60 days from date of submission', lastEditedById:'u2', lastEditedAt:'2025-12-10T09:00:00Z' },
  { code:'B', isComplete:true, content:'MCB Civil & Electrical (MCB CE) is a Queensland-based specialist contractor with over 12 years of experience in traffic signal, ITS, and civil electrical infrastructure. We hold current TMR Pre-Qualification for traffic signals (Category TS-A) and maintain a permanent team of licensed electricians, HV authorised persons, and certified traffic controllers.\n\nThis tender offers a fixed-price lump sum for the complete design and construction of the Sandgate Road / Earnshaw Road traffic signal upgrade, in full compliance with the specified MRTS standards. Our price includes all labour, materials, traffic management, and commissioning to TMR\'s satisfaction.', lastEditedById:'u2', lastEditedAt:'2025-12-10T09:00:00Z' },
  { code:'C', isComplete:true, content:'The works include:\n— Supply and installation of 8 signal poles with MRTS92-compliant footings\n— Supply and installation of UPVC conduit and cable pit network (MRTS91)\n— Supply and pulling of 3C+E 6mm² power cables (MRTS256)\n— Supply, mounting, and alignment of 24 LED signal heads (MRTS253)\n— Supply, fit-out, and commissioning of 1 × R900S SCATS controller (MRTS255/252)\n— SCATS functional test and TMR ITS acceptance (MRTS93 cl. 8)\n— Traffic management for the duration of works\n\nDrawing references: J1-E01 Rev B, J1-C01 Rev A', lastEditedById:'u2', lastEditedAt:'2025-12-10T09:00:00Z' },
  { code:'D', isComplete:true, content:'Works will be executed in the following sequence:\n1. Site establishment and traffic management mobilisation\n2. Civil works: excavation, footings, conduit installation, cable pit installation (concurrent)\n3. Electrical works: cable pulling, signal pole erection, head mounting and wiring\n4. Controller fit-out and SCATS configuration\n5. Pre-commissioning testing and SCATS functional test\n6. TMR ITS Inspector witness and acceptance\n7. Site reinstatement and demobilisation\n\nAll works to be executed per the referenced MRTS specifications. SOPs for each work task are held in the MCB CE Work Instructions management system.', lastEditedById:'u2', lastEditedAt:'2025-12-10T09:00:00Z' },
  { code:'E', isComplete:true, content:'[BOQ - see separate pricing tab]', lastEditedById:'u2', lastEditedAt:'2025-12-10T09:00:00Z' },
  { code:'F', isComplete:true, content:'[Auto-generated from BOQ]', lastEditedById:'u2', lastEditedAt:'2025-12-10T09:00:00Z' },
  { code:'G', isComplete:true, content:'This tender is based on the following qualifications and exclusions:\n— Excludes: TMR-supplied SCATS configuration file (client responsibility)\n— Excludes: Energex connection and metering costs (client to arrange)\n— Excludes: RPEQ footing certification (to be supplied by client per drawings)\n— Qualifies: Pricing based on normal working hours. Weekend or after-hours work subject to variation.\n— Qualifies: Any underground service conflicts identified during construction will be subject to a variation claim.', lastEditedById:'u2', lastEditedAt:'2025-12-10T09:00:00Z' },
  { code:'H', isComplete:true, content:'Proposed construction programme:\n— Start date: 15 January 2026 (subject to contract award by 20 December 2025)\n— Civil works completion: Week 4\n— Electrical installation: Weeks 3–6 (overlapping with civil)\n— Commissioning: Week 7\n— Practical completion: Week 8 (10 March 2026)\n\nFull bar chart programme available on request.', lastEditedById:'u2', lastEditedAt:'2025-12-10T09:00:00Z' },
  { code:'I', isComplete:true, content:'Project Manager: Jane Smith — 15 years signals experience, TMR Pre-Qual holder\nProject Coordinator: Sarah Lee — 8 years ITS construction\nLead Electrician: Tony Rivera — EL2 licence, TMR ITS accreditation\nSite Supervisor: Mike Walsh — TMR Pre-Qual holder', lastEditedById:'u2', lastEditedAt:'2025-12-10T09:00:00Z' },
  { code:'J', isComplete:true, content:'Appendix 1: Certificate of Currency — Public Liability $20M\nAppendix 2: Certificate of Currency — Professional Indemnity $5M\nAppendix 3: WorkCover QLD Certificate of Currency\nAppendix 4: TMR Pre-Qualification Certificate — Traffic Signals\nAppendix 5: MCB CE Company Profile and Project Experience', lastEditedById:'u2', lastEditedAt:'2025-12-10T09:00:00Z' },
]

// Tender 2: DRAFT — Virginia ITS (not yet started, just initiated)
const tdr002BOQ: BOQItem[] = [
  { id:'c01', sectionCode:'ELECTRICAL', type:'LABOUR', description:'ITS controller cabinet fit-out — labour (provisional)', quantity:1, unit:'item', unitCostBase:4800, markupPct:20, unitRateCharged:5760, totalCharged:5760, tradeCode:'EL2', isManualOverride:false },
  { id:'c02', sectionCode:'COMMISSIONING', type:'LABOUR', description:'Fibre optic splice and OTDR test per MRTS234 — labour', quantity:8, unit:'splices', unitCostBase:280, markupPct:20, unitRateCharged:336, totalCharged:2688, sopRef:'wi-sop-013', tradeCode:'EL2', isManualOverride:false },
  { id:'c03', sectionCode:'PRELIM_GENERAL', type:'PRELIM', description:'Site supervision and mob/demob (provisional)', quantity:1, unit:'item', unitCostBase:6500, markupPct:0, unitRateCharged:6500, totalCharged:6500, isManualOverride:false },
]

const tdr002Sections: TenderSectionContent[] = TENDER_SECTIONS.map(s => ({
  code: s.code as TenderSectionCode,
  isComplete: false,
  content: s.code === 'A' ? 'MCB Civil & Electrical Pty Ltd\nTender Reference: TDR-2026-002\nClient: Department of Transport and Main Roads\nRFT Reference: TMR-ITS-2026-00934\nProject: Virginia ITS Node Commissioning\n[DRAFT — not yet complete]' : '',
}))

// Tender 3: IN_PREPARATION — Maroochydore Rd
const tdr003BOQ: BOQItem[] = [
  { id:'d01', sectionCode:'CIVIL', type:'LABOUR', description:'Signal pole foundation (Type F-3) per MRTS92 — labour', quantity:6, unit:'each', unitCostBase:340, markupPct:20, unitRateCharged:408, totalCharged:2448, sopRef:'wi-sop-001', tradeCode:'CL2', isManualOverride:false },
  { id:'d02', sectionCode:'CIVIL', type:'MATERIAL', description:'Ready-mix concrete Grade 40 — foundations (supply)', quantity:6, unit:'each', unitCostBase:720, markupPct:15, unitRateCharged:828, totalCharged:4968, isManualOverride:false },
  { id:'d03', sectionCode:'CIVIL', type:'LABOUR', description:'Kerb & channel — machine lay', quantity:480, unit:'m', unitCostBase:28, markupPct:20, unitRateCharged:33.6, totalCharged:16128, tradeCode:'CL1', isManualOverride:false },
  { id:'d04', sectionCode:'CIVIL', type:'MATERIAL', description:'Concrete kerb & channel Type KS — supply & deliver', quantity:480, unit:'m', unitCostBase:42, markupPct:15, unitRateCharged:48.3, totalCharged:23184, isManualOverride:false },
  { id:'d05', sectionCode:'CIVIL', type:'SUBCONTRACTOR', description:'Asphalt pavement — subcontract (Downer EDI)', quantity:1, unit:'item', unitCostBase:148000, markupPct:10, unitRateCharged:162800, totalCharged:162800, isManualOverride:false },
  { id:'d06', sectionCode:'ELECTRICAL', type:'LABOUR', description:'LED signal head mount & align per MRTS253 (council supply)', quantity:24, unit:'each', unitCostBase:350, markupPct:20, unitRateCharged:420, totalCharged:10080, sopRef:'wi-sop-004', tradeCode:'EL2', isManualOverride:false },
  { id:'d07', sectionCode:'ELECTRICAL', type:'LABOUR', description:'SCATS controller fit-out per MRTS255 (council supply)', quantity:1, unit:'item', unitCostBase:3800, markupPct:20, unitRateCharged:4560, totalCharged:4560, sopRef:'wi-sop-005', tradeCode:'EL2', isManualOverride:false },
  { id:'d08', sectionCode:'ELECTRICAL', type:'LABOUR', description:'3C+E 6mm² cable pull per MRTS256', quantity:640, unit:'m', unitCostBase:6, markupPct:20, unitRateCharged:7.2, totalCharged:4608, sopRef:'wi-sop-003', tradeCode:'EL2', isManualOverride:false },
  { id:'d09', sectionCode:'ELECTRICAL', type:'MATERIAL', description:'3C+E 6mm² Olex power cable supply', quantity:640, unit:'m', unitCostBase:14, markupPct:15, unitRateCharged:16.1, totalCharged:10304, isManualOverride:false },
  { id:'d10', sectionCode:'TRAFFIC_CONTROL', type:'PRELIM', description:'Traffic management — single-lane alternating (9 months)', quantity:180, unit:'days', unitCostBase:880, markupPct:0, unitRateCharged:880, totalCharged:158400, isManualOverride:true, manualOverrideNote:'Manually priced — TC subcontract to Ventia. Rate agreed 22 Jun 2026.' },
  { id:'d11', sectionCode:'PRELIM_GENERAL', type:'PRELIM', description:'Site supervision / PC — 9-month programme', quantity:39, unit:'weeks', unitCostBase:2400, markupPct:0, unitRateCharged:2400, totalCharged:93600, isManualOverride:false },
  { id:'d12', sectionCode:'PRELIM_GENERAL', type:'PRELIM', description:'Mob/demob, safety plan, SWMS, site amenities', quantity:1, unit:'item', unitCostBase:12000, markupPct:0, unitRateCharged:12000, totalCharged:12000, isManualOverride:false },
]

const tdr003Markup: MarkupConfig = { labourOverheadPct:20, materialMarginPct:15, subcontractorMarginPct:10, contingencyPct:5, profitMarginPct:9 }

const tdr003Sections: TenderSectionContent[] = [
  { code:'A', isComplete:true, content:'MCB Civil & Electrical Pty Ltd\nTender Reference: TDR-2026-003\nClient: Sunshine Coast Council\nRFT Reference: SCC-CIVIL-2026-0041\nProject: Maroochydore Rd Intersection Reconstruction\nTender Closing: 28 June 2026 @ 12:00 pm\n[IN PREPARATION — for PM review]', lastEditedById:'u4', lastEditedAt:'2026-06-20T10:00:00Z' },
  { code:'B', isComplete:false, content:'[DRAFT] MCB CE is a Queensland specialist contractor with significant experience in intersection reconstruction and traffic signal installation. We offer a comprehensive fixed-price tender for all civil and electrical works.\n\n[PC NOTE: Jane to review and expand this section — add recent project examples]', lastEditedById:'u4', lastEditedAt:'2026-06-20T10:00:00Z' },
  { code:'C', isComplete:true, content:'Scope includes:\n— Stage 1: Kerb and channel works (480 lineal metres)\n— Stage 2: Asphalt pavement reconstruction (subcontract to Downer)\n— Stage 3: Traffic signal installation — poles, heads, controller, cables (council-supplied signal equipment; MCB CE to install)\n— Traffic management for the full 9-month construction programme\n\nDrawing references: J3-DWG-001 Rev A (19 drawings)', lastEditedById:'u4', lastEditedAt:'2026-06-21T14:00:00Z' },
  { code:'D', isComplete:false, content:'[DRAFT — to be completed by PM]', lastEditedById:'u4', lastEditedAt:'2026-06-20T10:00:00Z' },
  { code:'E', isComplete:true, content:'[BOQ — see pricing]', lastEditedById:'u4', lastEditedAt:'2026-06-22T09:00:00Z' },
  { code:'F', isComplete:true, content:'[Auto-generated from BOQ]', lastEditedById:'u4', lastEditedAt:'2026-06-22T09:00:00Z' },
  { code:'G', isComplete:false, content:'[DRAFT qualifications — PM to review]\n— Excludes: council-supplied signal heads, controller cabinet (install only)\n— Excludes: utility service relocations\n— Qualifies: programme subject to council approval of traffic management plan\n— Qualifies: asphalt quantity based on indicative drawings — subject to survey confirmation', lastEditedById:'u4', lastEditedAt:'2026-06-21T16:00:00Z' },
  { code:'H', isComplete:false, content:'[DRAFT programme — PM to review]\nProposed programme:\n— Civil Stage 1 (kerb): Months 1–3\n— Civil Stage 2 (asphalt subcontract): Months 3–6\n— Signal installation: Months 5–8\n— Commissioning: Month 8–9\n— Practical completion: Month 9', lastEditedById:'u4', lastEditedAt:'2026-06-21T16:00:00Z' },
  { code:'I', isComplete:false, content:'[To be completed — confirm key personnel with PM]', lastEditedById:'u4', lastEditedAt:'2026-06-20T10:00:00Z' },
  { code:'J', isComplete:false, content:'[Appendices to be attached before submission]', lastEditedById:'u4', lastEditedAt:'2026-06-20T10:00:00Z' },
]

// ── MOCK_TENDERS array ─────────────────────────────────────────────

export const MOCK_TENDERS: Tender[] = [
  {
    id: 'tdr-001', reference: 'TDR-2026-001',
    jobId: 'j1', rftReference: 'TMR-CS-2025-04417',
    clientName: 'Department of Transport and Main Roads', clientType: 'TMR',
    projectName: 'Sandgate Rd / Earnshaw Rd Traffic Signal Upgrade',
    status: 'AWARDED',
    submissionDeadline: '2025-12-12',
    validityPeriodDays: 60,
    sourceDocumentIds: ['doc-001','doc-002','doc-003'],
    markupConfig: defaultMarkup,
    boqItems: tdr001BOQ,
    sections: tdr001Sections,
    summary: calcTenderSummary(tdr001BOQ, defaultMarkup),
    submittedAt: '2025-12-10T14:30:00Z', submittedById: 'u2', submittedByName: 'Jane Smith',
    awardedAmount: 153240,
    outcomeRecordedAt: '2025-12-22T10:00:00Z',
    createdById: 'u2', createdByName: 'Jane Smith',
    createdAt: '2025-12-01T09:00:00Z', updatedAt: '2025-12-22T10:00:00Z',
  },
  {
    id: 'tdr-002', reference: 'TDR-2026-002',
    jobId: 'j4', rftReference: 'TMR-ITS-2026-00934',
    clientName: 'Department of Transport and Main Roads', clientType: 'TMR',
    projectName: 'Virginia ITS Node Commissioning',
    status: 'DRAFT',
    submissionDeadline: '2026-07-18',
    validityPeriodDays: 60,
    sourceDocumentIds: ['doc-008'],
    markupConfig: defaultMarkup,
    boqItems: tdr002BOQ,
    sections: tdr002Sections,
    summary: calcTenderSummary(tdr002BOQ, defaultMarkup),
    createdById: 'u2', createdByName: 'Jane Smith',
    createdAt: '2026-06-20T11:00:00Z', updatedAt: '2026-06-20T11:00:00Z',
  },
  {
    id: 'tdr-003', reference: 'TDR-2026-003',
    jobId: 'j3', rftReference: 'SCC-CIVIL-2026-0041',
    clientName: 'Sunshine Coast Council', clientType: 'COUNCIL',
    projectName: 'Maroochydore Rd Intersection Reconstruction',
    status: 'IN_PREPARATION',
    submissionDeadline: '2026-06-28',
    validityPeriodDays: 90,
    sourceDocumentIds: ['doc-004','doc-005','doc-006','doc-007'],
    markupConfig: tdr003Markup,
    boqItems: tdr003BOQ,
    sections: tdr003Sections,
    summary: calcTenderSummary(tdr003BOQ, tdr003Markup),
    pmReviewNotes: '',
    createdById: 'u4', createdByName: 'Sarah Lee',
    createdAt: '2026-06-18T09:00:00Z', updatedAt: '2026-06-22T09:00:00Z',
  },
]

// ── Helpers ───────────────────────────────────────────────────────

export function getTendersForJob(jobId: string): Tender[] {
  return MOCK_TENDERS.filter(t => t.jobId === jobId)
}

export function getDocumentsForJob(jobId: string): ClientDocument[] {
  return MOCK_CLIENT_DOCUMENTS.filter(d => d.jobId === jobId)
}

export function getActiveTenders(): Tender[] {
  return MOCK_TENDERS.filter(t => !['AWARDED','UNSUCCESSFUL','WITHDRAWN'].includes(t.status))
}

export function countSectionsDone(t: Tender): number {
  return t.sections.filter(s => s.isComplete).length
}

export function getDaysUntilDeadline(deadline?: string): number | null {
  if (!deadline) return null
  return Math.ceil((new Date(deadline).getTime() - Date.now()) / 86400000)
}
